/**
 * The words of a harvest's text messages (ADR-142 decision 3, PROTOCOL.md 6.89.0): `harvest`, the page's request naming
 * a prop by its index, and `harvestState`, the server's answer. Their field tables are in `text.ts`, which `encodeText`
 * and `parseTextFrame` apply.
 */

/**
 * The states of a `harvestState`: the hold starts (with the tick it ends), ends, is broken, or the request is refused.
 */
export const HARVEST_STATES = ["hold", "done", "broken", "refused"] as const;

/**
 * Why a `harvest` is refused outright, nothing held: the character holds no tool of the kind the prop needs (`tool`),
 * stands out of reach (`reach`), the prop is already harvested or the page's index names none (`gone`), the grant would
 * not fit the bag (`full`), a hold is running or the bag is busy (`busy`), the character is dead (`dead`), held by a
 * Game Master (`frozen`) or in combat (`combat`), or the map has no such prop (`unknown`).
 */
export const HARVEST_REFUSALS = ["tool", "reach", "gone", "full", "busy", "dead", "frozen", "combat", "unknown"] as const;
export type HarvestRefusal = (typeof HARVEST_REFUSALS)[number];

/**
 * Why a running hold ends without a harvest: the character moved or was hurt, combat began, the character acted (an
 * ability, a target, the flag, the respawn, an item used, moved, sorted or dropped, a travel channel begun), someone
 * else took the prop (`gone`), the bag filled (`full`), the character died or was held, the tool left the belt or the
 * bag (`tool`), the world was swapped (`swap`) or the character left the map (`left`).
 */
export const HARVEST_BREAKS = ["moved", "damaged", "combat", "acted", "gone", "full", "dead", "frozen", "tool", "swap", "left"] as const;
export type HarvestBreak = (typeof HARVEST_BREAKS)[number];

/**
 * A `harvestState` reason as the wire carries it: 1 to 16 lower-case letters, so a reason a later server names is a
 * value a page words generically and not a malformed frame.
 */
export const HARVEST_REASON_PATTERN = /^[a-z]+$/;

/**
 * The longest `harvestState` reason on the wire.
 */
export const MAX_HARVEST_REASON_CHARS = 16;
